import React, { useEffect, useState } from 'react';
import {
  Dialog,
  Button,
  FormControl,
  DialogTitle,
  DialogActions,
  IconButton,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Close from '@mui/icons-material/Close';
import { DateTimePicker } from 'components/common/DateTimePicker';
import moment, { Moment } from 'moment';
import { IInspection } from 'redux/conductInspectionSlice';

import './RescheduleInspectionModal.scss';
import toast from 'react-hot-toast';
import { useRescheduleInspectionMutation } from 'redux/services/inspection';
import { useParams } from 'react-router-dom';

type RescheduleInspectionModalProps = {
  open: boolean;
  inspection: IInspection | null;
  onClose: () => void;
  onSuccess: () => void;
};

export const RescheduleInspectionModal: React.FC<
  RescheduleInspectionModalProps
> = ({ open, inspection, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(
    moment(inspection?.scheduledDate!).toISOString(),
  );

  const [rescheduleInspection] = useRescheduleInspectionMutation();

  useEffect(() => {
    if (inspection) {
      setDate(moment(inspection.scheduledDate!).toISOString());
    }
  }, [inspection]);

  const handleSubmit = async () => {
    if (date === moment(inspection!.scheduledDate!).toISOString()) {
      toast.error(t('error_no_change_in_scheduled_date'));
    } else {
      const res: any = await rescheduleInspection({
        id: inspection?._id,
        projectId,
        body: {
          scheduledDate: date,
          rescheduleReason: reason,
        },
      });

      if (res.data) {
        toast.success(t('success_inspection_rescheduled'));
        onSuccess();
        handleClose();
      } else {
        toast.error(res.error?.data?.message ?? t('something_went_wrong'));
      }
    }
  };

  const handleClose = () => {
    setReason('');
    setDate('');
    onClose();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setReason(e.target.value);
  const handleDateChange = (value: Moment) => setDate(value.toISOString());

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="reschedule-inspection-modal"
      PaperProps={{
        style: {
          padding: 0,
          minWidth: 300,
        },
      }}
    >
      <DialogActions className="dialog-actions d-flex justify-content-between w-100">
        <DialogTitle className="p-1" color="inherit">
          {t('modal_title_reschedule_inspection')}
        </DialogTitle>
        <IconButton className="close-btn" onClick={onClose}>
          <Close color="inherit" />
        </IconButton>
      </DialogActions>
      <FormControl className="p-4">
        <DateTimePicker
          value={moment(date).toDate()}
          label={t('label_schedule_date_time')}
          placement="auto-start"
          onChange={handleDateChange}
        />
        <TextField
          name={t('placeholder_reason_for_reschedule')}
          className="mt-4"
          multiline
          required
          value={reason}
          label={t('placeholder_reason_for_reschedule')}
          maxRows={4}
          onChange={handleChange}
        />
        <footer className="p-0 mt-4 d-flex flex-row justify-content-between flex-grow-1 align-items-end">
          <Button variant="outlined" onClick={onClose}>
            {t('back')}
          </Button>
          <Button
            disabled={reason.length === 0 || date.length === 0}
            variant="contained"
            onClick={handleSubmit}
          >
            {t('btn_reschedule')}
          </Button>
        </footer>
      </FormControl>
    </Dialog>
  );
};
